import { useEffect, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { isEmpty, sortBy } from "lodash";
import useReferenceApi from "@services/api/reference/reference.api";
import { ReferenceProps } from "@services/api/reference/reference.types";
import { YearProps } from "@services/api/years/years.types";
import useYears from "../../hooks/years.hook";

export default function useReference() {
  const { getYear } = useYears();
  function isActive(reference: ReferenceProps, year?: YearProps) {
    if (!year) {
      return true;
    }
    if (reference.year) {
      return reference.year.externalId === year.externalId;
    }

    const started = !reference.startDate || reference.startDate <= year.endDate;
    const open = !reference.endDate || reference.endDate >= year.startDate;
    return started && open;
  }

  const { useFindByOneQuery } = useReferenceApi();
  const getActiveReferences = async (
    type?: string,
    yearExternalId?: string
  ): Promise<ReferenceProps[]> => {
    if (!type) {
      return [];
    }

    const references = await findReferences(type);
    const currentYear = getYear(yearExternalId);
    return references.filter((reference) => isActive(reference, currentYear));
  };

  async function findReferences(type: string) {
    const references = await useFindByOneQuery.fetcher(type);
    return sortBy(references, ["sequence", "externalId"]) as ReferenceProps[];
  }

  const types = [
    "TIME-BLOCK",
    "FACULTY_DESCRIPTION",
    "STUDY_DESCRIPTION",
    "MODULEGROUP_DESCRIPTION",
    "MODULE_DESCRIPTION",
    "SOFTWARE",
    "PHASE"
  ];

  const [references, setReferences] =
    useState<Record<string, ReferenceProps[]>>();

  const [...fetchedReferences] = useQueries({
    queries: types.map((type) => ({
      queryKey: [type],
      queryFn: async () => {
        const references = await findReferences(type);
        return { [type]: references };
      }
    }))
  });

  function getReferenceById(
    types: ReferenceProps[],
    externalId: string | undefined
  ) {
    if (isEmpty(externalId)) {
      return null;
    }

    return types.find((type) => type.externalId === externalId);
  }

  useEffect(() => {
    if (references) return;
    if (!fetchedReferences?.every(({ data }) => data)) return;

    fetchedReferences.forEach(({ data }) => {
      if (!data) return;
    });

    const mappedReferences = fetchedReferences
      .map(({ data }) => data)
      .reduce((prev, curr) => ({ ...prev, ...curr }), {});
    setReferences(mappedReferences);
  }, [fetchedReferences, references]);

  return {
    references,
    getActiveReferences,
    getReferenceById
  };
}
